import React, { useState, useEffect } from "react";
import axios from "axios";
import { useCruise } from "../context/CruiseContext";
import { Camera, Play, Quote, Star, Flag } from "lucide-react";
import ReviewForm from "./ReviewForm";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const { GALLERY, TESTIMONIALS, EVENT } = useCruise();
  const [playing, setPlaying] = useState(false);
  const [reviews, setReviews] = useState(TESTIMONIALS);
  const [reported, setReported] = useState({});

  const reportReview = async (id) => {
    if (!id || reported[id]) return;
    if (!window.confirm("Report this review as inappropriate? Our team will review it.")) return;
    try {
      await axios.post(`${API}/reviews/${id}/report`);
      setReported((p) => ({ ...p, [id]: true }));
      toast.success("Thanks — this review has been flagged for our team.");
    } catch {
      toast.error("Couldn't submit the report. Please try again.");
    }
  };

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/reviews`, { params: { cruise: EVENT.id } })
      .then((res) => {
        if (mounted && Array.isArray(res.data) && res.data.length) {
          const approved = res.data.map((r) => ({
            quote: r.quote,
            name: r.name,
            trip: r.trip || "House Heads At Sea",
            initials: r.initials || "HH",
            photo: r.photo || "",
          }));
          setReviews([...approved, ...TESTIMONIALS].slice(0, 8));
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <section id="gallery" className="relative bg-[#111111] py-24">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center mb-14">
          <p className="font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm mb-3">From Past Sailings</p>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl">
            The <span className="text-[#E50914]">Highlights</span>
          </h2>
          <div className="flex justify-center mt-4">
            <span className="section-line" />
          </div>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto">
            A taste of the energy — packed dance floors, pool parties, theme nights and island adventures
            from the House Heads At Sea family.
          </p>
        </div>

        {/* Featured recap video reel */}
        <div className="relative mb-6 overflow-hidden rounded-2xl border border-white/10 bg-black">
          {playing ? (
            <video
              src="/media/hhas-recap-2025.mp4"
              className="w-full max-h-[560px] bg-black"
              controls
              autoPlay
              playsInline
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="group relative block w-full"
              aria-label="Play recap video"
            >
              <img
                src="/gallery/poolparty.webp"
                alt="House Heads At Sea 2025 recap"
                className="w-full h-[280px] sm:h-[440px] object-cover opacity-70 group-hover:opacity-90 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="flex items-center justify-center w-20 h-20 rounded-full bg-[#E50914] text-white shadow-2xl transition-transform group-hover:scale-110">
                  <Play size={34} className="ml-1 fill-white" />
                </span>
                <p className="mt-5 font-cond uppercase tracking-[0.3em] text-white text-sm sm:text-base">
                  Watch The 2025 Recap
                </p>
              </div>
              <span className="absolute top-4 left-4 bg-[#E50914] text-white font-cond uppercase text-[10px] tracking-widest px-3 py-1">
                Video Reel
              </span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-3">
          {GALLERY.map((item, i) => (
            <div
              key={i}
              className={`group relative overflow-hidden rounded-xl ${item.span}`}
            >
              <img
                src={item.src}
                alt={item.caption}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 p-4 translate-y-1 group-hover:translate-y-0 transition-transform">
                <div className="flex items-center gap-2 text-[var(--hh-gold)] mb-1">
                  <Camera size={14} />
                </div>
                <p className="font-cond font-bold uppercase text-white text-base sm:text-lg tracking-wide">
                  {item.caption}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials from past cruisers */}
        <div className="mt-16">
          <p className="text-center font-cond uppercase tracking-[0.35em] text-[var(--hh-gold)] text-xs sm:text-sm mb-8">
            What Past Cruisers Are Saying
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reviews.map((t, i) => (
              <div
                key={i}
                className="relative bg-[#1A1A1A] border border-white/10 rounded-2xl hover:border-[#E50914]/50 p-6 flex flex-col transition-colors"
              >
                <Quote size={26} className="text-[#E50914]/40 mb-3" />
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={13} className="text-[var(--hh-gold)] fill-[var(--hh-gold)]" />
                  ))}
                </div>
                <p className="text-white/80 text-sm leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/10">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-[var(--hh-gold)] shrink-0"
                    />
                  ) : (
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#E50914] text-white font-cond font-bold text-sm shrink-0">
                      {t.initials}
                    </span>
                  )}
                  <div>
                    <p className="font-cond uppercase text-white text-sm tracking-wide leading-none">{t.name}</p>
                    <p className="text-white/40 text-xs mt-1">{t.trip}</p>
                  </div>
                </div>
                {t.id && (
                  <button
                    onClick={() => reportReview(t.id)}
                    disabled={reported[t.id]}
                    className="absolute top-4 right-4 inline-flex items-center gap-1 text-white/25 hover:text-[#E50914] text-[10px] font-cond uppercase tracking-widest transition-colors disabled:text-[#1B8A5A]"
                    title="Report this review"
                  >
                    <Flag size={11} /> {reported[t.id] ? "Flagged" : "Report"}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center mt-10">
            <p className="text-white/60 text-sm mb-4">Sailed with us before? We'd love to hear your story.</p>
            <ReviewForm />
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-8 uppercase font-cond tracking-widest">
          Real moments from past sailings — yours are next
        </p>
      </div>
    </section>
  );
};

export default Gallery;
